import { api } from '@/lib/api';

// Slug values must match the tag.name created in DMS under tag_group=published_page.
export type PublishedPageSlug = 'vto' | 'playing_to_win' | 'bmc' | 'vpc';

type DmsTagListItem = {
  id: string;
  name: string;
  group: { name: string };
};
type DmsTagListResponse = { data: DmsTagListItem[] };

type DmsDocument = {
  id: string;
  name: string;
  updatedAt: string;
  // A list response carries no body: `content` / `content_text` are not in the
  // list projection (ATE-12094), so the body must be read from
  // `GET /api/v4/dms/documents/:id/content`. Typing it here would compile clean
  // against an absent field and render an empty page with no error.
  currentVersion: {
    version: number;
    updatedAt?: string;
  } | null;
};
type DmsDocumentListResponse = { data: DmsDocument[] };

/** `GET /api/v4/dms/documents/:id/content` — the only body read path. */
type DmsDocumentContentResponse = {
  documentId: string;
  version: number;
  content: string;
  checksum: string | null;
  updatedAt: string;
  needsConversion?: boolean;
};

export type PublishedPage = {
  documentId: string;
  documentName: string;
  version: number;
  updatedAt: string;
  content: string;
};

async function findTagId(slug: PublishedPageSlug): Promise<string> {
  const tags = await api.get<DmsTagListResponse>('/api/v4/dms/tags');
  const match = tags.data.find(
    (t) => t.group?.name === 'published_page' && t.name === slug
  );
  if (!match) {
    throw new Error(`No DMS tag found for published_page=${slug}`);
  }
  return match.id;
}

export async function fetchPublishedPage(slug: PublishedPageSlug): Promise<PublishedPage> {
  const tagId = await findTagId(slug);
  const docs = await api.get<DmsDocumentListResponse>('/api/v4/dms/documents', {
    tags: tagId,
    pageLength: 1,
  });
  const doc = docs.data[0];
  if (!doc) {
    throw new Error(`No DMS document tagged published_page=${slug}`);
  }
  if (!doc.currentVersion) {
    throw new Error(`DMS document ${doc.name} has no current version`);
  }
  const body = await api.get<DmsDocumentContentResponse>(
    `/api/v4/dms/documents/${doc.id}/content`
  );
  if (typeof body?.content !== 'string') {
    throw new Error(`DMS document ${doc.name} returned no content`);
  }
  return {
    documentId: doc.id,
    documentName: doc.name,
    version: body.version ?? doc.currentVersion.version,
    updatedAt: body.updatedAt ?? doc.currentVersion.updatedAt ?? doc.updatedAt,
    content: body.content,
  };
}
